import { useCallback, useEffect, useMemo, useState } from "react";

export const stockUnitIdentity = (option) => `${option.level}:${option.unitId ?? option.label}`;

function readPreferences(key) {
  if (!key) return {};
  try {
    const value = JSON.parse(localStorage.getItem(key) || "{}");
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
}

// Only display choices are stored. Quantities, prices and conversion factors always
// come from the latest inventory response, never from browser preferences.
export default function useStockDisplayUnits(userId, businessUnitId) {
  const key = userId && businessUnitId
    ? `pilcore:current-stock-units:v1:${userId}:${businessUnitId}`
    : null;
  const stored = useMemo(() => readPreferences(key), [key]);
  const [state, setState] = useState(() => ({ key, choices: stored }));
  const choices = state.key === key ? state.choices : stored;

  useEffect(() => {
    const refresh = (event) => {
      if (event.key === key || event.key === null) {
        setState({ key, choices: readPreferences(key) });
      }
    };
    window.addEventListener("storage", refresh);
    return () => window.removeEventListener("storage", refresh);
  }, [key]);

  const selectUnit = useCallback((itemId, option) => {
    if (!itemId || !option) return;
    const next = { ...choices };
    if (option.level === "unit") delete next[itemId];
    else next[itemId] = stockUnitIdentity(option);
    setState({ key, choices: next });
    if (key) {
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Disabled/full browser storage must not prevent changing the view.
      }
    }
  }, [key, choices]);

  return { choices, selectUnit };
}
